import * as THREE from "three";
import { clampFillOpacity } from "./fillOpacity";

type ObjectOpacityMaterialState = {
  /** Intrinsic opacity before object fillOpacity is applied. */
  opacity: number;
  transparent: boolean;
  depthWrite: boolean;
  appliedOpacity: number;
  appliedTransparent: boolean;
  appliedDepthWrite: boolean;
  appliedObjectOpacity: number;
};

const OPACITY_EPSILON = 0.001;
const STATE_KEY = "objectFillOpacityState";

function materiallyDifferent(left: number, right: number): boolean {
  return Math.abs(left - right) > OPACITY_EPSILON;
}

/**
 * Multiplies every material under `root` by object fill opacity.
 *
 * Used so Line-heavy symbols (doors, windows, furniture strokes) fade
 * uniformly on live slider updates — the same end state a remount/refresh
 * would produce. Skips fully invisible hitbox materials (opacity 0).
 */
export function setObjectMaterialOpacity(
  root: THREE.Object3D,
  objectOpacity: number,
): void {
  const clampedOpacity = clampFillOpacity(objectOpacity);
  root.traverse((object) => {
    const materialOwner = object as THREE.Object3D & {
      material?: THREE.Material | THREE.Material[];
    };
    if (!materialOwner.material) return;
    const materials = Array.isArray(materialOwner.material)
      ? materialOwner.material
      : [materialOwner.material];
    materials.forEach((material) => {
      let state = material.userData[STATE_KEY] as
        | ObjectOpacityMaterialState
        | undefined;
      if (!state) {
        // Invisible hit targets stay invisible; don't treat them as content.
        if (material.opacity <= OPACITY_EPSILON && material.transparent) {
          return;
        }
        state = {
          opacity: material.opacity,
          transparent: material.transparent,
          depthWrite: material.depthWrite,
          appliedOpacity: material.opacity,
          appliedTransparent: material.transparent,
          appliedDepthWrite: material.depthWrite,
          appliedObjectOpacity: 1,
        };
        material.userData[STATE_KEY] = state;
      } else if (
        materiallyDifferent(material.opacity, state.appliedOpacity) ||
        material.transparent !== state.appliedTransparent ||
        material.depthWrite !== state.appliedDepthWrite
      ) {
        // External writer changed the material (R3F props, scene fade, etc.).
        // Recover the intrinsic baseline by dividing out the last object opacity.
        const divisor = Math.max(state.appliedObjectOpacity, OPACITY_EPSILON);
        state.opacity = material.opacity / divisor;
        if (clampedOpacity >= 0.999) {
          state.transparent = material.transparent;
          state.depthWrite = material.depthWrite;
        }
      }

      const nextOpacity = state.opacity * clampedOpacity;
      const nextTransparent = state.transparent || nextOpacity < 1;
      const nextDepthWrite = nextOpacity < 1 ? false : state.depthWrite;
      if (
        materiallyDifferent(material.opacity, nextOpacity) ||
        material.transparent !== nextTransparent ||
        material.depthWrite !== nextDepthWrite
      ) {
        material.opacity = nextOpacity;
        material.transparent = nextTransparent;
        material.depthWrite = nextDepthWrite;
        material.needsUpdate = true;
      }
      state.appliedOpacity = nextOpacity;
      state.appliedTransparent = nextTransparent;
      state.appliedDepthWrite = nextDepthWrite;
      state.appliedObjectOpacity = clampedOpacity;
    });
  });
}
